// src/components/Appointments/AdminAssignmentModal.jsx
// ✅ FIXED VERSION - Aligned with backend API structure

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiX,
  FiUser,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiLoader,
  FiZap,
  FiCalendar,
  FiRefreshCw,
} from "react-icons/fi";
import Button from "../../../components/common/Button";
import {
  getAvailableAdminsThunk,
  reassignAdminThunk,
  unassignAdminThunk,
  autoAssignAdminThunk,
} from "../../../store/thunks/appointmentThunks";
import { clearAvailableAdmins } from "../../../store/slices/appointmentSlice";
import toast from "react-hot-toast";

const AdminAssignmentModal = ({ isOpen, onClose, appointment, onSuccess }) => {
  const dispatch = useDispatch();
  const { availableAdmins, unavailableAdmins, loadingAvailableAdmins } =
    useSelector((state) => state.appointment);

  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [assignmentReason, setAssignmentReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);

  useEffect(() => {
    if (isOpen && appointment) {
      // ✅ FIXED: Send appointmentType field that matches backend expectations
      dispatch(
        getAvailableAdminsThunk({
          branchCode: appointment.branchCode,
          scheduledDateTime: appointment.scheduledDateTime,
          duration: appointment.duration,
          appointmentType: appointment.appointmentType, // ✅ FIXED: Using appointmentType
          appointmentId: appointment._id,
        })
      );
    }

    return () => {
      dispatch(clearAvailableAdmins());
    };
  }, [isOpen, appointment, dispatch]);

  if (!isOpen) return null;

  const hasCurrentAdmin = appointment?.adminCode;

  const handleAssignAdmin = async () => {
    if (!selectedAdmin) {
      toast.error("Please select an admin");
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        reassignAdminThunk({
          appointmentId: appointment._id,
          newAdminCode: selectedAdmin.adminCode,
          reason: assignmentReason || "Admin assignment updated",
        })
      ).unwrap();

      toast.success("Admin assigned successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error || "Failed to assign admin");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignAdmin = async () => {
    if (!hasCurrentAdmin) return;

    setSubmitting(true);
    try {
      await dispatch(
        unassignAdminThunk({
          appointmentId: appointment._id,
          reason: assignmentReason || "Admin unassigned",
        })
      ).unwrap();

      toast.success("Admin unassigned successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error || "Failed to unassign admin");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoAssign = async () => {
    setSubmitting(true);
    try {
      await dispatch(autoAssignAdminThunk(appointment._id)).unwrap();

      toast.success("Admin auto-assigned successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error || "Failed to auto-assign admin");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = () => {
    dispatch(
      getAvailableAdminsThunk({
        branchCode: appointment.branchCode,
        scheduledDateTime: appointment.scheduledDateTime,
        duration: appointment.duration,
        appointmentType: appointment.appointmentType, // ✅ FIXED: Using appointmentType
        appointmentId: appointment._id,
      })
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-light flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-text">Admin Assignment</h2>
            <p className="text-sm text-neutral-dark mt-1">
              {appointment?.appointmentCode} •{" "}
              {new Date(appointment?.scheduledDateTime).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-dark hover:text-text transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Current Assignment */}
          {hasCurrentAdmin && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-dark mb-1">
                    Currently Assigned To:
                  </p>
                  <div className="flex items-center gap-2">
                    <FiUser className="text-primary" />
                    <span className="font-semibold text-text">
                      {appointment.adminName}
                    </span>
                    <span className="text-sm text-neutral-dark">
                      ({appointment.adminCode})
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnassignAdmin}
                  disabled={submitting}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Unassign
                </Button>
              </div>
            </div>
          )}

          {/* Auto-Assign Button */}
          <div className="mb-6">
            <Button
              variant="primary"
              size="md"
              onClick={handleAutoAssign}
              disabled={submitting || loadingAvailableAdmins}
              className="w-full"
              icon={<FiZap />}
            >
              Auto-Assign Best Available Admin
            </Button>
            <p className="text-xs text-neutral-dark mt-2 text-center">
              Automatically assigns the admin with the lowest workload
            </p>
          </div>

          <div className="border-t border-neutral-light pt-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-text">
                {loadingAvailableAdmins
                  ? "Checking availability..."
                  : `Available Admins (${availableAdmins?.length || 0})`}
              </h3>
              <button
                onClick={handleRefresh}
                disabled={loadingAvailableAdmins}
                className="text-primary hover:text-primary-dark transition-colors"
              >
                <FiRefreshCw
                  className={loadingAvailableAdmins ? "animate-spin" : ""}
                  size={18}
                />
              </button>
            </div>

            {/* Loading State */}
            {loadingAvailableAdmins && (
              <div className="flex items-center justify-center py-8">
                <FiLoader className="animate-spin text-primary" size={32} />
              </div>
            )}

            {/* Available Admins List */}
            {!loadingAvailableAdmins && availableAdmins && (
              <div className="space-y-2">
                {availableAdmins.length === 0 && (
                  <div className="text-center py-8">
                    <FiAlertCircle
                      className="mx-auto text-neutral-dark mb-2"
                      size={48}
                    />
                    <p className="text-neutral-dark">
                      No admins available for this time slot
                    </p>
                  </div>
                )}

                {availableAdmins.map((admin) => (
                  <div
                    key={admin.adminCode}
                    onClick={() => setSelectedAdmin(admin)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedAdmin?.adminCode === admin.adminCode
                        ? "border-primary bg-primary-light"
                        : "border-neutral-light hover:border-primary-light"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            selectedAdmin?.adminCode === admin.adminCode
                              ? "bg-primary text-white"
                              : "bg-neutral-light text-neutral-dark"
                          }`}
                        >
                          <FiUser size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-text">
                            {admin.adminName}
                          </p>
                          <p className="text-sm text-neutral-dark">
                            {admin.adminCode}
                            {admin.specialization &&
                              ` • ${admin.specialization}`}
                          </p>
                        </div>
                      </div>
                      {selectedAdmin?.adminCode === admin.adminCode && (
                        <FiCheckCircle className="text-primary" size={24} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unavailable Admins Toggle */}
          {!loadingAvailableAdmins &&
            unavailableAdmins &&
            unavailableAdmins.length > 0 && (
              <div className="border-t border-neutral-light pt-4">
                <button
                  onClick={() => setShowUnavailable(!showUnavailable)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h3 className="font-semibold text-neutral-dark">
                    Unavailable Admins ({unavailableAdmins.length})
                  </h3>
                  <FiAlertCircle className="text-neutral-dark" />
                </button>

                {showUnavailable && (
                  <div className="mt-4 space-y-2">
                    {unavailableAdmins.map((admin) => (
                      <div
                        key={admin.adminCode}
                        className="p-4 rounded-lg border-2 border-neutral-light bg-gray-50 opacity-60"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <FiUser size={20} className="text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-text">
                                {admin.adminName}
                              </p>
                              <p className="text-sm text-red-600">
                                {admin.reason || "Not available"}
                              </p>
                            </div>
                          </div>
                          <FiAlertCircle className="text-red-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          {/* Assignment Reason */}
          {selectedAdmin && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-text mb-2">
                Assignment Reason (Optional)
              </label>
              <textarea
                value={assignmentReason}
                onChange={(e) => setAssignmentReason(e.target.value)}
                rows="3"
                className="w-full px-4 py-2 border border-neutral-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Explain why you're assigning this admin..."
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-light flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleAssignAdmin}
            disabled={!selectedAdmin || submitting}
            className="flex-1"
          >
            {submitting ? "Assigning..." : "Assign Admin"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAssignmentModal;
